/**
 * PostDetailPage — iter242 (User-Wunsch nach Post-Deep-Link-Route).
 *
 * Deep-Link-Ziel für /posts/:post_id. Rendert einen einzelnen Post im
 * bestehenden `FeedCard`-Layout, damit Paywall/Delivery-URL/Owned-Badge
 * konsistent zum Feed dargestellt werden.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import FeedCard from "@/components/feed/FeedCard";

export default function PostDetailPage() {
  const { post_id: postId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(`/posts/${postId}`);
        if (!cancelled) setPost(data);
      } catch (e) {
        if (!cancelled) {
          setErr(
            e?.response?.status === 404
              ? t("post.not_found", { defaultValue: "Dieser Beitrag existiert nicht mehr." })
              : e?.response?.data?.detail || "Fehler beim Laden"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId, t]);

  return (
    <div data-testid="post-detail-page" className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-30 bg-white border-b border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto flex items-center gap-2 px-4 h-14">
          <button
            data-testid="post-detail-back"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"
            aria-label={t("common.back", { defaultValue: "Zurück" })}
          >
            <ArrowLeft size={18} className="text-[#0A0A0A]" />
          </button>
          <p className="text-sm font-semibold text-[#0A0A0A]">
            {t("post.detail_title", { defaultValue: "Beitrag" })}
          </p>
        </div>
      </header>
      <main className="max-w-2xl mx-auto py-6 px-2 sm:px-4">
        {loading ? (
          <div data-testid="post-detail-loading" className="flex items-center justify-center py-24 text-[#666]">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : err ? (
          <div data-testid="post-detail-error" className="p-6 border border-[#E5E5E5] rounded-2xl bg-white text-center text-sm text-[#666]">
            {err}
          </div>
        ) : post ? (
          <FeedCard post={post} onRefresh={() => window.location.reload()} />
        ) : null}
      </main>
    </div>
  );
}
